'use client';

import { COLORS } from '@/constants';
import {
  BookmarkIcon,
  BubbleChatIcon,
  HomeIcon,
  HouseKeyIcon,
  ArrowDown,
  TextField,
} from '@/components';
import { MenuItem, Typography } from '@mui/material';
import { useTranslations } from 'next-intl';
import { FC, ReactNode, useState } from 'react';
import { useGetOfficeStatistics } from '@/queris';
import { StatsGrid, StatsSectionHeader, StatsSectionWrapper } from '../styled';
import { DASHBOARD_PERIOD_DAYS, DASHBOARD_PERIOD_OPTIONS, DashboardPeriod } from '../constants';
import { formatStatCount } from '../utils';
import { DashboardStatCard } from './DashboardStatCard';
import { OfficeAdminStatisticsRo } from '@/types';

type StatCardConfig = {
  labelKey:
    | 'activeForSale'
    | 'activeForRent'
    | 'alleImmobilienInfoRequests'
    | 'disabledForSale'
    | 'disabledForRent'
    | 'inquiries'
    | 'watchlist'
    | 'wlasDeliveredInfoRequests'
    | 'wlasReceivedInfoRequests';
  field: keyof OfficeAdminStatisticsRo;
  icon: ReactNode;
};

const STAT_CARDS: StatCardConfig[] = [
  {
    labelKey: 'activeForSale',
    field: 'activeForSaleCount',
    icon: <HomeIcon width={24} height={24} />,
  },
  {
    labelKey: 'activeForRent',
    field: 'activeForRentCount',
    icon: <HouseKeyIcon width={24} height={24} />,
  },
  {
    labelKey: 'disabledForSale',
    field: 'disabledForSaleCount',
    icon: <HomeIcon width={24} height={24} />,
  },
  {
    labelKey: 'disabledForRent',
    field: 'disabledForRentCount',
    icon: <HouseKeyIcon width={24} height={24} />,
  },
  {
    labelKey: 'watchlist',
    field: 'watchlistImmosCount',
    icon: <BookmarkIcon width={24} height={24} />,
  },
  {
    labelKey: 'inquiries',
    field: 'infoRequestsCount',
    icon: <BubbleChatIcon width={24} height={24} />,
  },
  {
    labelKey: 'alleImmobilienInfoRequests',
    field: 'alleImmobilienInfoRequestsCount',
    icon: <BubbleChatIcon width={24} height={24} />,
  },
  {
    labelKey: 'wlasReceivedInfoRequests',
    field: 'wlasReceivedInfoRequestsCount',
    icon: <BubbleChatIcon width={24} height={24} />,
  },
  {
    labelKey: 'wlasDeliveredInfoRequests',
    field: 'wlasDeliveredInfoRequestsCount',
    icon: <BubbleChatIcon width={24} height={24} />,
  },
];

export const DashboardStatsSection: FC = () => {
  const t = useTranslations('dashboard');
  const [period, setPeriod] = useState<DashboardPeriod>('last30Days');
  const { data, isFetching } = useGetOfficeStatistics(DASHBOARD_PERIOD_DAYS[period]);

  return (
    <StatsSectionWrapper>
      <StatsSectionHeader>
        <Typography fontSize={26} fontWeight={700} lineHeight={1} color={COLORS['primary/900']}>
          {t('stats')}
        </Typography>

        <TextField
          select
          name="dashboardPeriod"
          value={period}
          onChange={(e) => setPeriod(e.target.value as DashboardPeriod)}
          size="small"
          sx={{ width: '154px', background: COLORS.white, borderRadius: '6px' }}
          SelectProps={{
            MenuProps: {
              PaperProps: { style: { maxHeight: 200 } },
            },
            IconComponent: (props) => <ArrowDown {...props} />,
          }}
        >
          {DASHBOARD_PERIOD_OPTIONS.map((key) => (
            <MenuItem key={key} value={key}>
              {t(`period.${key}`)}
            </MenuItem>
          ))}
        </TextField>
      </StatsSectionHeader>

      <StatsGrid>
        {STAT_CARDS.map((item) => (
          <DashboardStatCard
            key={item.labelKey}
            icon={item.icon}
            label={t(`statCards.${item.labelKey}`)}
            value={formatStatCount(data?.[item.field])}
            isLoading={isFetching}
          />
        ))}
      </StatsGrid>
    </StatsSectionWrapper>
  );
};
